import type { AppliedFrameIdentity } from './applied-frame';

export const APPLIED_FRAME_MAX_PARTS = 64;
export interface AppliedFramePart {
    type: 'janus_applied_part'; actor_id: string; encounter_id: string; operation_id: string;
    index: number; count: number; payload: string;
    encoding?: 'gzip';
    dice?: { schema: string; kind: string; campaign_id: string };
}
/** Bound each socket request below 128 KB even after JSON escaping and UTF-8. */
export function appliedFrameParts(wire: string, identity: Pick<AppliedFrameIdentity, 'actor_id' | 'encounter_id' | 'operation_id'>): AppliedFramePart[] {
    const { actor_id, encounter_id, operation_id } = identity;
    const count = Math.ceil(wire.length / 32768);
    return Array.from({ length: count }, (_, index) => ({ type: 'janus_applied_part', actor_id, encounter_id, operation_id,
        index, count, payload: wire.slice(index * 32768, (index + 1) * 32768) }));
}

/** Compression starts only after local apply. It retains every saved input and
 * derived field while keeping newborn definition closures within socket limits. */
export async function compressedAppliedFrameParts(wire: string, identity: Pick<AppliedFrameIdentity, 'actor_id' | 'encounter_id' | 'operation_id'>): Promise<AppliedFramePart[]> {
    if (typeof CompressionStream === 'undefined') return appliedFrameParts(wire, identity);
    const compressed = new Uint8Array(await new Response(new Blob([wire]).stream().pipeThrough(new CompressionStream('gzip'))).arrayBuffer());
    let binary = '';
    for (let at = 0; at < compressed.length; at += 16384) binary += String.fromCharCode(...compressed.subarray(at, at + 16384));
    return appliedFrameParts(btoa(binary), identity).map(part => ({ ...part, encoding: 'gzip' }));
}

/** One collector per socket bundle. Parts are never admitted as actions; only
 * the completed original frame reaches operation dedup and the shared installer. */
export function createAppliedFrameParts() {
    const held = new Map<string, { at: number; identity: string; count: number; parts: Map<number, string> }>();
    return (part: AppliedFramePart): unknown => {
        const now = Date.now();
        for (const [id, row] of held) if (now - row.at > 30000) held.delete(id);
        if (!Number.isInteger(part.count) || part.count < 1 || part.count > APPLIED_FRAME_MAX_PARTS || !Number.isInteger(part.index)
            || part.index < 0 || part.index >= part.count || typeof part.payload !== 'string' || part.payload.length > 32768) return;
        const identity = JSON.stringify([part.actor_id, part.encounter_id, part.encoding, part.dice]);
        let row = held.get(part.operation_id);
        if (row && (row.identity !== identity || row.count !== part.count)) return;
        if (!row) {
            if (held.size >= 32) held.delete(held.keys().next().value!);
            row = { at: now, identity, count: part.count, parts: new Map() }; held.set(part.operation_id, row);
        }
        row.parts.set(part.index, part.payload);
        if (row.parts.size !== row.count) return;
        held.delete(part.operation_id);
        const joined = Array.from({ length: row.count }, (_, i) => row.parts.get(i)).join('');
        const decode = (wire: string): unknown => {
            try {
                const value = JSON.parse(wire) as Record<string, unknown>;
                if (part.dice) {
                    const command = value.command as Record<string, unknown> | undefined;
                    if (value.type !== 'janus_dice_truth' || !command || command.schema !== part.dice.schema || command.kind !== part.dice.kind
                        || value.campaign_id !== part.dice.campaign_id || command.campaign_id !== part.dice.campaign_id
                        || command.actor_id !== part.actor_id || command.encounter_id !== part.encounter_id
                        || command.schema === 'janus.roll.truth.v1' && (value.applied_frame || command.applied_frame)) return;
                }
                if (value.operation_id === part.operation_id && value.actor_id === part.actor_id && value.encounter_id === part.encounter_id) return value;
            } catch { /* A damaged transfer never prevents the saved-row relay. */ }
        };
        if (part.encoding !== 'gzip') return decode(joined);
        try {
            const bytes = Uint8Array.from(atob(joined), char => char.charCodeAt(0));
            return new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))).text().then(decode, () => undefined);
        } catch { /* The saved-row relay remains available. */ }
    };
}
